import { useCallback, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Popover, Overlay } from "react-bootstrap";
import QuickLinks from "./QuickLinks";
import "./help.scss";
import { Grid, useMantineColorScheme, Select, TextInput, Textarea, FileInput } from "@mantine/core";
import { IconFileSearch } from "@tabler/icons-react";
import { useAtom } from "jotai";
import { tourAtom } from "../../utils/atoms/tourAtom";
import { useLocation } from "react-router-dom";
import helpJson from "./helpCardMenu.json";
import MGAAPI from "../../api";
import SEConfirmModal from "../common/SEConfirmModal";
import { getErrorMessage } from "../../utils/errorUtils";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { TICKET_TYPE_OPTIONS } from "../../constants/helper";
import { scopeListAccessAtom } from "../../utils/atoms/scopeListAccessAtom";

const PopoverListItem = ({ icon, title, text, onClick }) => {
  return (
    <div
      className="popover-item d-flex align-items-center mb-2"
      style={onClick ? { cursor: "pointer" } : undefined}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick(event);
              }
            }
          : undefined
      }
    >
      <div className="popover-item-icon-container">
        <se-icon className="mr-1" size="large" color="secondary">
          {icon}
        </se-icon>
      </div>
      <div>
        <h5 className="popover-item-title mb-0">{title}</h5>
        <p className="popover-item-text mb-0">{text}</p>
      </div>
    </div>
  );
};

const PopoverList = ({ onRaiseTicket, canUserSubmitTicket }) => {
  // Always use dark mode
  return (
    <div>
      {helpJson?.helpContent?.map((listItem) => (
        (listItem?.title !== "Raise a ticket" || canUserSubmitTicket) && (
          <PopoverListItem
            key={listItem?.icon}
            icon={listItem?.icon}
            title={listItem?.title}
            text={listItem?.text}
            onClick={listItem?.title === "Raise a ticket" ? onRaiseTicket : undefined}
          />
        )
      ))}
    </div>
  );
};

const HelpSection = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [popoverTarget, setPopoverTarget] = useState(null);
  const [popoverShow, setPopoverShow] = useState(false);
  const [demoConfirmOpen, setDemoConfirmOpen] = useState(false);
  const [isCreatingDemo, setIsCreatingDemo] = useState(false);
  const { colorScheme } = useMantineColorScheme();
  const MGA_API = new MGAAPI();
  const [projectDetails] = useAtom(projectDetailsAtom);

  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpForm, setHelpForm] = useState({
    ticketType: "TARIFF_REVIEW",
    ticketTitle: "",
    ticketDesc: "",
    file: null,
  });
  const [helpSubmitting, setHelpSubmitting] = useState(false);
  const [helpErrors, setHelpErrors] = useState({
    ticketType: "",
    ticketTitle: "",
    ticketDesc: "",
  });

  const [scopeListAccess] = useAtom(scopeListAccessAtom);

  const canUserSubmitTicket = scopeListAccess?.submitTicket ?? false;

  const resetHelpForm = () => {
    setHelpForm({
      ticketType: "TARIFF_REVIEW",
      ticketTitle: "",
      ticketDesc: "",
      file: null,
    });
    setHelpErrors({ ticketType: "", ticketTitle: "", ticketDesc: "" });
  };

  const handleHelpFieldChange = (field, value) => {
    setHelpForm((prev) => ({ ...prev, [field]: value }));
    setHelpErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleHelpFileChange = (file) => {
    setHelpForm((prev) => ({ ...prev, file: file || null }));
  };

  const handleRaiseTicket = () => {
    closePopover();
    resetHelpForm();
    setShowHelpModal(true);
  };

  const handleHelpSubmit = () => {
    const nextErrors = {};

    if (!helpForm.ticketType) {
      nextErrors.ticketType = t("grid.getHelp.ticketTypeRequired");
    }

    const trimmedTitle = (helpForm.ticketTitle || "").trim();
    if (!trimmedTitle) {
      nextErrors.ticketTitle = t("grid.getHelp.titleRequired");
    } else if (trimmedTitle.length < 5) {
      nextErrors.ticketTitle = t("grid.getHelp.titleTooShort");
    } else if (trimmedTitle.length > 100) {
      nextErrors.ticketTitle = t("grid.getHelp.titleTooLong");
    }

    const trimmedDesc = (helpForm.ticketDesc || "").trim();
    if (!trimmedDesc) {
      nextErrors.ticketDesc = t("grid.getHelp.descriptionRequired");
    }

    setHelpErrors({
      ticketType: nextErrors.ticketType || "",
      ticketTitle: nextErrors.ticketTitle || "",
      ticketDesc: nextErrors.ticketDesc || "",
    });

    if (Object.keys(nextErrors).length > 0) return;

    const datasetPayload = {
      zipCode: projectDetails?.locationDTO?.zipCode || "",
      countryName: projectDetails?.locationDTO?.country || "",
      page: location?.pathname || "",
    };

    const formData = new FormData();
    formData.append("ticketType", helpForm.ticketType);
    formData.append("ticketTitle", trimmedTitle);
    formData.append("ticketDesc", trimmedDesc);
    if (helpForm.file) {
      formData.append("file", helpForm.file);
    }
    formData.append("dataSet", JSON.stringify(datasetPayload));
    formData.append("status", "OPEN");

    setHelpSubmitting(true);
    MGA_API.submitTicket(formData)
      .then(() => {
        callSnackbar("success", t("grid.getHelp.success"));
        resetHelpForm();
        setShowHelpModal(false);
      })
      .catch((error) => {
        console.error("Help ticket submission failed", error);
        callSnackbar("error", getErrorMessage(error));
      })
      .finally(() => {
        setHelpSubmitting(false);
      });
  };

  const snackBarRef = useRef(null);
  const [snackBar, setSnackBar] = useState({ type: "info", message: "", duration: 3000 });

  const callSnackbar = useCallback((type, message, duration = 3000) => {
    setSnackBar({ type, message, duration });
    if (snackBarRef.current) {
      try {
        snackBarRef.current.open = true;
        snackBarRef.current.canClose = type === "error";
      } catch (e) {
        // ignore DOM/custom-element errors
      }
    }
  }, []);

  const showPopover = (e) => {
    setPopoverTarget(e.target);
    setPopoverShow((prevState) => !prevState); // Fix: toggle boolean directly
  };
  const closePopover = () => {
    setPopoverTarget(null);
    setPopoverShow(false);
  };
  const [, setTour] = useAtom(tourAtom);

  const handleStartTour = () => {
    closePopover();
    // Remove position: fixed from <se-app> if present
    const seApp = document.querySelector("se-app");
    if (seApp && seApp.style.position === "fixed") {
      seApp.style.position = "";
    }
    setTour(true);
  };

  const handleDemoProject = () => {
    closePopover();
    setDemoConfirmOpen(true);
  };

  const handleCancelDemoProject = () => {
    setDemoConfirmOpen(false);
  };

  const handleConfirmDemoProject = async () => {
    setIsCreatingDemo(true);
    try {
      const demoProject = await MGA_API.createDemoProject({ projectName: "Demo Project" });
      const highlightProjectId =
        demoProject?.projectId ||
        demoProject?.data?.projectId ||
        demoProject?.id ||
        demoProject?.data?.id;
      setDemoConfirmOpen(false);
      window.dispatchEvent(
        new CustomEvent("mga-projects-refresh", {
          detail: { highlightProjectId },
        })
      );
      callSnackbar(
        "success",
        t("help.demoProjectCreated")
      );
    } catch (err) {
      console.error("Failed to create demo project", err);
 setDemoConfirmOpen(false);
      callSnackbar(
        "error",
        getErrorMessage(err) ||
          t("help.demoProjectCreateFailed"),
        8000
      );

    } finally {
      setIsCreatingDemo(false);
    }
  };
  
  return (
    <>
      <div className="help-tray-container">
        <se-button
          aria-label="help-open-tray"
          option="raised"
          dir="right"
          color="secondary"
          onClick={(e) => showPopover(e)}
        >
          <div className="help-button d-flex align-items-center justify-content-center">
            <span className="help-button-text mr-1">{t('help.title')}</span>
            <se-icon size="small">help_stroke</se-icon>
          </div>
        </se-button>
        {popoverTarget && (
          <Overlay
            target={popoverTarget}
            show={popoverShow}
            placement="left"
            rootClose
            onHide={closePopover}
          >
            <Popover
              className="helpPopup"
              style={{ color: colorScheme === "dark" ? "white" : "black" }}
            >
              <div
                id="help-popover"
                style={{
                  backgroundColor: colorScheme === "dark" ? "#1f2729" : "white",
                }}
              >
                <div className="popover-head px-3">
                  <h5 className="popover-title border-bottom px-1 py-2">
                    {t('help.needHelp')}
                  </h5>
                  <button
                    type="button"
                    className="popover-close-button close"
                    aria-label="help-popover-close"
                    onClick={() => closePopover()}
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                    }}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="popover-content">
                  <div>
                    <PopoverList
                      onRaiseTicket={handleRaiseTicket}
                      canUserSubmitTicket={canUserSubmitTicket}
                    />
                    <QuickLinks handleStartTour={handleStartTour} handleDemoProject={handleDemoProject}/>
                  </div>
                </div>
              </div>
            </Popover>
          </Overlay>
        )}
      </div>
      <SEConfirmModal
        opened={demoConfirmOpen}
        onClose={handleCancelDemoProject}
        onConfirm={handleConfirmDemoProject}
        header="Create demo project"
        message="This will create a demo project with sample data. Do you want to proceed?"
        confirmLabel="Create"
        cancelLabel="Cancel"
        confirmLoading={isCreatingDemo}
        confirmLoadingLabel="Creating..."
      />

      {showHelpModal && (
      <SEConfirmModal
        opened={showHelpModal}
        onClose={() => {
          resetHelpForm();
          setShowHelpModal(false);
        }}
        header={t("grid.getHelp.title", "Submit a Support Ticket")}
        onConfirm={handleHelpSubmit}
        cancelLabel={t("common.cancel")}
        confirmLabel={t("common.ok")}
        size="xl"
        confirmLoading={helpSubmitting}
        confirmLoadingLabel={t("common.submitting")}
        modalPadding="xl"
        message={
          <>
            <Select
              label={t("grid.getHelp.issueTypeLabel")}
              withAsterisk
              data={TICKET_TYPE_OPTIONS}
              value={helpForm.ticketType}
              error={helpErrors.ticketType}
              onChange={(value) => handleHelpFieldChange("ticketType", value)}
            />
            <TextInput
              label={t("grid.getHelp.titleLabel")}
              placeholder={t("grid.getHelp.titlePlaceholder")}
              withAsterisk
              minLength={5}
              maxLength={100}
              value={helpForm.ticketTitle}
              error={helpErrors.ticketTitle}
              onChange={(event) =>
                handleHelpFieldChange("ticketTitle", event.currentTarget.value)
              }
            />
            <Textarea
              placeholder={t("grid.getHelp.descriptionPlaceholder")}
              label={t("grid.getHelp.descriptionLabel")}
              withAsterisk
              minRows={10}
              value={helpForm.ticketDesc}
              error={helpErrors.ticketDesc}
              onChange={(event) =>
                handleHelpFieldChange("ticketDesc", event.currentTarget.value)
              }
            />
            <FileInput
              label={t("grid.getHelp.attachmentLabel")}
              accept="image/*"
              placeholder={t("grid.getHelp.attachmentPlaceholder")}
              value={helpForm.file}
              onChange={handleHelpFileChange}
              clearable
              rightSection={<IconFileSearch size={18} />}
              rightSectionProps={{ style: { pointerEvents: "none" } }}
            />
          </>
        }
      />
      )}

      <se-snackbar
        ref={snackBarRef}
        id="snackbar-help"
        type={snackBar.type}
        icon="information_stroke"
        message={snackBar.message}
        duration={`${snackBar.duration ?? 3000}`}
      ></se-snackbar>
    </>
  );
};

export default HelpSection;